import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControlLabel,
  Grid2,
  List,
  Popover,
  Radio,
  styled,
  Typography,
} from "@mui/material";
import {
  PrimaryFormActionButton,
  PrimaryTooltip,
} from "../../../Components/StyledComponent";
import SearchInput from "./SearchInput";
import FilterSelectBox from "./FilterSelectBox";
import { useTranslation } from "react-i18next";

const popoverPaperStyles = {
  py: "20px",
  px: "16px",
  borderRadius: "10px",
  backgroundColor: "#FFFFFF",
  boxShadow:
    "2px 2px 4px 0px rgba(151, 151, 151, 0.25), -2px -2px 4px 0px rgba(151, 151, 151, 0.25)",
};

const ItemRowRadioLabel = styled(FormControlLabel)(({ theme }) => ({
  display: "block",
  marginLeft: 0,

  ".MuiButtonBase-root": {
    padding: 0,
  },

  ".MuiRadio-root": {
    padding: 0,
    color: "#A2A2A2 !important",

    "&.Mui-checked": {
      color: "#6D72F6 !important",
    },
    "&.Mui-disabled": {
      color: "#A2A2A2 !important",
    },
  },

  ".MuiSvgIcon-root": {
    width: "20px",
    height: "20px",
  },
}));

/**
 * Options are plain strings (value === label, the original API) or
 * { value, label } objects. Exported for tests.
 */
export const normaliseSelectOptions = (options) =>
  (Array.isArray(options) ? options : [])
    .map((opt) =>
      opt !== null && typeof opt === "object"
        ? { value: opt.value, label: opt.label ?? String(opt.value) }
        : { value: opt, label: opt }
    )
    .filter((opt) => opt.value !== null && opt.value !== undefined);

/* Component */
// Additive props (all optional, defaults keep the original behaviour):
//   options      - strings, or { value, label } objects; onChange gets the value
//   dataTestId   - test id on the box; options get `${dataTestId}-option-${value}`,
//                  Apply gets `${dataTestId}-apply`
//   hideBoxLabel - no notched label on the box (caller renders a label above it)
//   height       - fixed box height, to line up with other controls in a row
//   searchable   - false hides the search input (short lists)
const SingleSelectDropdown = ({
  selectedOption,
  options,
  label,
  onChange,
  dataTestId,
  hideBoxLabel = false,
  height,
  searchable = true,
}) => {
  const { t } = useTranslation(["common"]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selected, setSelected] = useState(selectedOption);
  const [search, setSearch] = useState("");

  const open = Boolean(anchorEl);
  const items = useMemo(() => normaliseSelectOptions(options), [options]);
  const labelOf = (value) => items.find((item) => item.value === value)?.label;

  /* ---------- UI ---------- */
  const filteredOptions = useMemo(() => {
    if (!search.trim()) return items;
    return items.filter((item) =>
      String(item.label).toLowerCase().includes(search.toLowerCase())
    );
  }, [items, search]);

  const handleOnApply = () => {
    onChange(selected);
    setAnchorEl(null);
  };

  const handleSelection = (value) => {
    setSelected(value);
  };

  useEffect(() => {
    if (open) {
      setSelected(selectedOption);
    }
  }, [open]);

  useEffect(() => {
    setSelected(selectedOption);
  }, [selectedOption]);

  useEffect(() => {
    if (items.length > 0 && !selectedOption) {
      setSelected(items[0].value);
    }
  }, [items]);

  if (items.length === 1) {
    return (
      <Box>
        <FilterSelectBox
          open={false}
          onClick={() => {}}
          label={label}
          displayValue={items[0].label}
          disabled={true}
          dataTestId={dataTestId}
          hideLabel={hideBoxLabel}
          height={height}
        />
      </Box>
    );
  }

  return (
    <>
      <Box>
        <FilterSelectBox
          open={open}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          label={label}
          displayValue={labelOf(selectedOption) ?? (selectedOption || `Select ${label}`)}
          dataTestId={dataTestId}
          hideLabel={hideBoxLabel}
          height={height}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: popoverPaperStyles } }}
      >
        <Grid2
          container
          flexDirection={"column"}
          gap={2}
          sx={{ width: "315px" }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="fw700s14lh24c3D4B5A"
              sx={{ textTransform: "capitalize" }}
            >
              Select {label}
            </Typography>
          </Box>

          {searchable && (
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${label}`}
            />
          )}

          <Box sx={{ height: searchable ? "200px" : "auto", maxHeight: "200px", overflowY: "auto" }}>
            <List disablePadding>
              {filteredOptions.map((option, index) => {
                return (
                  <Grid2
                    key={index}
                    container
                    gap={2}
                    alignItems="center"
                    sx={{ py: 0.5 }}
                  >
                    <ItemRowRadioLabel
                      key={String(option.value)}
                      disabled={false}
                      data-testid={dataTestId ? `${dataTestId}-option-${option.value}` : undefined}
                      control={
                        <Radio
                          checked={selected === option.value}
                          onChange={() => handleSelection(option.value)}
                        />
                      }
                      label={
                        <Typography
                          variant={
                            true ? "fw400s12lh24c3D4B5A" : "fw400s12lh24c989898"
                          }
                        >
                          {" "}
                          {option.label}{" "}
                        </Typography>
                      }
                    />
                  </Grid2>
                );
              })}
            </List>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <PrimaryFormActionButton
              onClick={handleOnApply}
              disabled={!selected}
              data-testid={dataTestId ? `${dataTestId}-apply` : undefined}
            >
              Apply
            </PrimaryFormActionButton>
          </Box>
        </Grid2>
      </Popover>
    </>
  );
};

export default SingleSelectDropdown;
